import { useMemo, useState } from 'react';
import { Trophy, Medal, Award, Star, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { categoryDef } from '@/lib/constants';
import { formatVND, formatVNDShort, formatPercent } from '@/lib/format';
import type { ReportMonitor } from '@/lib/api';

type SortMetric = 'units' | 'revenue' | 'margin';

export interface ReportTopSkusTableProps {
  monitors: ReportMonitor[];
  /** Default 15 — Phase 4 partner-report requirement */
  maxRows?: number;
  /** Hide internal columns (cost basis, margin %) for partner-facing reports */
  showInternalColumns?: boolean;
}

const SORT_LABELS: Record<SortMetric, string> = {
  units: 'Số lượng',
  revenue: 'Doanh thu',
  margin: 'Margin %',
};

function rankBadge(rank: number) {
  if (rank === 1) return <Trophy className="h-3.5 w-3.5 text-amber-500" />;
  if (rank === 2) return <Medal className="h-3.5 w-3.5 text-slate-400" />;
  if (rank === 3) return <Award className="h-3.5 w-3.5 text-amber-700" />;
  return null;
}

export function ReportTopSkusTable({
  monitors,
  maxRows = 15,
  showInternalColumns = true,
}: ReportTopSkusTableProps) {
  const [sortBy, setSortBy] = useState<SortMetric>('units');

  const sorted = useMemo(() => {
    const copy = [...monitors];
    copy.sort((a, b) => {
      if (sortBy === 'units') return b.sales_units - a.sales_units;
      if (sortBy === 'revenue') return b.sales_revenue - a.sales_revenue;
      // margin %: filter out zero-revenue rows so they don't pollute the top
      const am = a.sales_revenue > 0 ? (a.sales_margin / a.sales_revenue) * 100 : -Infinity;
      const bm = b.sales_revenue > 0 ? (b.sales_margin / b.sales_revenue) * 100 : -Infinity;
      return bm - am;
    });
    return copy.slice(0, maxRows).filter((m) => m.sales_units > 0 || m.sales_revenue > 0);
  }, [monitors, sortBy, maxRows]);

  if (sorted.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-6 text-center text-xs text-muted-foreground">
        Chưa có SKU nào có sales trong period này.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Sort toggle pills */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-muted-foreground">Sắp xếp theo:</span>
        {(Object.keys(SORT_LABELS) as SortMetric[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setSortBy(m)}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 font-medium transition-colors',
              sortBy === m
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            {SORT_LABELS[m]}
            {sortBy === m && <ArrowDown className="h-3 w-3" />}
          </button>
        ))}
      </div>

      {/* Mobile: card list — table 6 cols bị bóp ở 375px viewport. Hiển thị 3 metrics ngang dưới
          để partner thấy đủ data cho từng SKU mà không phải scroll ngang. */}
      <div className="space-y-2 md:hidden">
        {sorted.map((m, idx) => {
          const rank = idx + 1;
          const cat = categoryDef(m.category);
          const specs = [
            m.size ? `${m.size}"` : null,
            m.resolution,
            m.refresh_rate ? `${m.refresh_rate}Hz` : null,
          ].filter(Boolean) as string[];
          const marginPct = m.sales_revenue > 0 ? (m.sales_margin / m.sales_revenue) * 100 : null;
          const metricCount = showInternalColumns ? 3 : 2;
          return (
            <div
              key={m.id}
              className={cn(
                'rounded-lg border bg-card p-3',
                rank <= 3 && 'bg-amber-50/40',
                m.is_focal === 1 && 'bg-cat-focal/5'
              )}
            >
              {/* Top: rank + name + sku */}
              <div className="flex items-start gap-2">
                <div className="flex w-8 shrink-0 items-center justify-center gap-0.5">
                  <span className={cn('text-sm font-bold tabular-nums', rank <= 3 && 'text-amber-700')}>
                    {rank}
                  </span>
                  {rankBadge(rank)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-sm font-medium leading-tight">{m.name}</div>
                  <div className="mt-1 flex items-center gap-1">
                    {m.is_focal === 1 && (
                      <Star className="h-2.5 w-2.5 shrink-0 fill-cat-focal text-cat-focal" />
                    )}
                    <span className="truncate font-mono text-[10px] uppercase text-muted-foreground">
                      {m.id}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                    {cat && (
                      <span className="inline-flex items-center gap-0.5">
                        <cat.icon className={cn('h-2.5 w-2.5', cat.colorClass)} />
                        {cat.label}
                      </span>
                    )}
                    {specs.length > 0 && <span>· {specs.join(' · ')}</span>}
                  </div>
                </div>
              </div>

              {/* Bottom: metrics in 2 or 3 columns (depends on internal cols visibility) */}
              <div
                className={cn(
                  'mt-2 grid gap-2 border-t pt-2 text-center',
                  metricCount === 3 ? 'grid-cols-3' : 'grid-cols-2'
                )}
              >
                <div>
                  <div className="text-base font-bold tabular-nums leading-none">
                    {m.sales_units.toLocaleString('vi-VN')}
                  </div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">Số lượng</div>
                </div>
                <div>
                  <div className="text-base font-bold tabular-nums leading-none text-primary">
                    {formatVNDShort(m.sales_revenue)}
                  </div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">Doanh thu</div>
                </div>
                {showInternalColumns && (
                  <div>
                    <div
                      className={cn(
                        'text-base font-bold tabular-nums leading-none',
                        marginPct != null && marginPct < 0 && 'text-destructive',
                        marginPct != null && marginPct >= 10 && 'text-cat-graphic'
                      )}
                    >
                      {marginPct != null ? formatPercent(marginPct, 1) : '—'}
                    </div>
                    <div className="mt-0.5 text-[10px] text-muted-foreground">% LN</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tablet+: full table with horizontal scroll if needed */}
      <div className="hidden overflow-x-auto rounded-lg border bg-card print:block print:rounded-md print:shadow-none md:block">
        <table className="w-full border-collapse text-[11px]">
          <thead className="bg-primary text-primary-foreground">
            <tr>
              <th className="w-10 px-2 py-2 text-center font-semibold uppercase tracking-wider">#</th>
              <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider">SKU</th>
              <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider">Tên · Spec</th>
              <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">Units</th>
              <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">Doanh thu</th>
              {showInternalColumns && (
                <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">% LN</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((m, idx) => {
              const rank = idx + 1;
              const cat = categoryDef(m.category);
              const specs = [
                m.size ? `${m.size}"` : null,
                m.resolution,
                m.refresh_rate ? `${m.refresh_rate}Hz` : null,
              ].filter(Boolean) as string[];
              const marginPct =
                m.sales_revenue > 0 ? (m.sales_margin / m.sales_revenue) * 100 : null;
              return (
                <tr
                  key={m.id}
                  className={cn(
                    'border-b border-border print-no-break',
                    rank <= 3 && 'bg-amber-50/40',
                    m.is_focal === 1 && 'bg-cat-focal/5'
                  )}
                >
                  <td className="px-2 py-1.5 text-center">
                    <div className="inline-flex items-center justify-center gap-1 tabular-nums">
                      <span className={cn('font-semibold', rank <= 3 && 'text-amber-700')}>
                        {rank}
                      </span>
                      {rankBadge(rank)}
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      {m.is_focal === 1 && (
                        <Star className="h-2.5 w-2.5 fill-cat-focal text-cat-focal" />
                      )}
                      <span className="font-mono text-[10px]">{m.id}</span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="font-medium leading-tight">{m.name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                      {cat && (
                        <span className="inline-flex items-center gap-0.5">
                          <cat.icon className={cn('h-2.5 w-2.5', cat.colorClass)} />
                          {cat.label}
                        </span>
                      )}
                      {specs.length > 0 && <span>· {specs.join(' · ')}</span>}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                    {m.sales_units.toLocaleString('vi-VN')}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-primary">
                    {formatVND(m.sales_revenue)}
                  </td>
                  {showInternalColumns && (
                    <td
                      className={cn(
                        'px-2 py-1.5 text-right tabular-nums',
                        marginPct != null && marginPct < 0 && 'text-destructive',
                        marginPct != null && marginPct >= 10 && 'text-cat-graphic'
                      )}
                    >
                      {marginPct != null ? formatPercent(marginPct, 1) : '—'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
